import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button, Drawer, Form, Input, Select } from "antd";
import PropTypes from "prop-types";

import { Modals, Types } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";
import Rules from "scripts/rules";

import Upload from "components/Upload";
import { ObjectNumbersToEnglish } from "scripts/helpers";

const Component = ({ data = {}, onUpdate, options: { cities = [] } }) => {
	const initialState = {};
	const [state, setState] = useState(initialState);
	const { submitting, visible } = state;
	const { id, firstName, lastName } = data;
	const { hash, pathname, search } = useLocation();
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const handleCompare = (rule, value) => {
		return !value || form.getFieldValue("password") === value
			? Promise.resolve()
			: Promise.reject(Resources.comparePass);
	};

	const handleClose = () => {
		navigate(-1);
	};

	const handleSubmit = () => {
		if (!submitting) {
			form.validateFields().then((values) => {
				setState((prevState) => ({ ...prevState, submitting: true }));

				values = ObjectNumbersToEnglish(values);

				values.avatarId = values.avatar?.id || 977;

				delete values.avatar;

				const action =
					hash === `#${Modals.CREATE}`
						? API.user.create(values)
						: API.user.update(id, values);

				action
					.then(() => {
						onUpdate(null, true);
						handleClose();
					})
					.catch(() => {
						setState((prevState) => ({
							...prevState,
							submitting: false,
						}));
					});
			});
		}
	};

	const componentDidUpdate = () => {
		switch (hash) {
			case `#${Modals.CREATE}`: {
				setState((prevState) => ({ ...prevState, visible: true }));

				break;
			}
			case `#${Modals.EDIT}`: {
				if (id) {
					setState((prevState) => ({
						...prevState,
						visible: true,
					}));

					form.setFieldsValue({ ...data, cityId: data.city?.id });
				} else {
					navigate(`${pathname}${search}`, { replace: true });
				}

				break;
			}
			default: {
				if (visible) form.resetFields();

				setState(initialState);

				break;
			}
		}
	};

	useEffect(componentDidUpdate, [hash]);

	return (
		<Drawer
			className="sp-form-drawer"
			footer={
				<>
					<Button type="primary" onClick={handleSubmit} loading={submitting}>
						{Resources.confirm}
					</Button>
					<Button onClick={() => handleClose()} loading={submitting}>
						{Resources.cancel}
					</Button>
				</>
			}
			onClose={() => handleClose()}
			title={
				hash === `#${Modals.CREATE}`
					? `${Resources.add} ${Resources.user}`
					: `${Resources.edit} ${Resources.user} "${firstName} ${lastName}"`
			}
			maskClosable={false}
			open={visible}
			width={414}
		>
			<Form layout="vertical" form={form}>
				<Form.Item
					name="firstName"
					label={Resources.name}
					rules={Rules.required}
				>
					<Input />
				</Form.Item>
				<Form.Item
					name="lastName"
					label={Resources.lastName}
					rules={Rules.required}
				>
					<Input />
				</Form.Item>
				<Form.Item name="phone" label={Resources.mobile} rules={Rules.required}>
					<Input type="tel" />
				</Form.Item>
				<Form.Item name="cityId" label={Resources.city} rules={Rules.required}>
					<Select options={cities} optionFilterProp="label" showSearch />
				</Form.Item>
				<Form.Item name="avatar" label={Resources.image}>
					<Upload section={Types.upload.user} />
				</Form.Item>
				<hr className="sp-divider" style={{ marginBottom: 16 }} />
				{hash === `#${Modals.CREATE}` ? (
					<>
						<Form.Item
							name="password"
							label={Resources.password}
							rules={Rules.required.concat(Rules.password)}
						>
							<Input.Password type="password" autoComplete="off" />
						</Form.Item>
						<Form.Item
							name="passwordConfirmation"
							label={`${Resources.repeat} ${Resources.password}`}
							rules={Rules.required.concat([{ validator: handleCompare }])}
						>
							<Input.Password type="password" autoComplete="off" />
						</Form.Item>
					</>
				) : (
					<>
						<Form.Item
							name="password"
							label={`${Resources.password} ${Resources.new}`}
							rules={Rules.password}
						>
							<Input.Password type="password" autoComplete="off" />
						</Form.Item>
						<Form.Item
							name="passwordConfirmation"
							label={`${Resources.repeat} ${Resources.password} ${Resources.new}`}
							rules={[{ validator: handleCompare }]}
						>
							<Input.Password type="password" autoComplete="off" />
						</Form.Item>
					</>
				)}
			</Form>
		</Drawer>
	);
};

Component.propTypes = {
	data: PropTypes.object,
	onUpdate: PropTypes.func.isRequired,
	options: PropTypes.object.isRequired,
};

export default Component;
